import { useState } from 'react';
import {
  MODEL_REGISTRY,
  modelUnsupportedReason,
  modelsForTask,
  type ModelEntry,
  type ModelTask,
} from '@design-ai/inference';
import { loadModel, type Engine, type ModelLoadState } from '../engine.js';
import { ProgressBar, Section, Select } from './controls.js';

export interface ModelPanelProps {
  engine: Engine;
  onLoaded: () => void;
}

const IDLE: ModelLoadState = { status: 'idle', progress: null };

export function ModelPanel({ engine, onLoaded }: ModelPanelProps) {
  const [matteId, setMatteId] = useState(MODEL_REGISTRY[0]!.id);
  const [upscaleId, setUpscaleId] = useState(modelsForTask('upscale')[0]!.id);
  const [matteState, setMatteState] = useState<ModelLoadState>(IDLE);
  const [upscaleState, setUpscaleState] = useState<ModelLoadState>(IDLE);

  const load = async (
    task: ModelTask,
    entry: ModelEntry,
    setState: (state: ModelLoadState) => void,
  ): Promise<void> => {
    try {
      const model = await loadModel(entry, setState);
      if (task === 'matting') await engine.workbench.setMattingModel(model, entry);
      else await engine.workbench.setUpscaleModel(model, entry);
      onLoaded();
    } catch (cause) {
      setState({
        status: 'error',
        progress: null,
        message: cause instanceof Error ? cause.message : String(cause),
      });
    }
  };

  return (
    <Section title="Models" subtitle="Downloaded once, then cached in the browser.">
      <ModelSlot
        task="matting"
        label="Background removal"
        selected={matteId}
        onSelect={setMatteId}
        state={matteState}
        caps={engine.ctx.caps}
        onLoad={(entry) => void load('matting', entry, setMatteState)}
      />
      <ModelSlot
        task="upscale"
        label="Upscaler"
        selected={upscaleId}
        onSelect={setUpscaleId}
        state={upscaleState}
        caps={engine.ctx.caps}
        onLoad={(entry) => void load('upscale', entry, setUpscaleState)}
      />
    </Section>
  );
}

function ModelSlot({
  task,
  label,
  selected,
  onSelect,
  state,
  caps,
  onLoad,
}: {
  task: ModelTask;
  label: string;
  selected: string;
  onSelect: (id: string) => void;
  state: ModelLoadState;
  caps: { shaderF16: boolean };
  onLoad: (entry: ModelEntry) => void;
}) {
  const options = modelsForTask(task);
  const entry = options.find((option) => option.id === selected) ?? options[0]!;
  const busy = state.status === 'downloading' || state.status === 'compiling';
  // Catch an unrunnable model before a 90 MB download, not after it.
  const unsupported = modelUnsupportedReason(entry, caps);

  return (
    <div className="model-slot">
      <Select
        label={label}
        value={entry.id}
        options={options.map((option) => ({ value: option.id, label: option.label }))}
        onChange={onSelect}
      />
      <p className="model-slot__notes">{entry.notes}</p>
      {/* Shown at the point of choosing rather than buried in docs. This is an
          internal, non-commercial tool, so a non-commercial licence is not a
          problem here - it is stated, not flagged as an error. */}
      <p
        className={`model-slot__license model-slot__license--${entry.commercialUse}`}
        title={entry.license}
      >
        {entry.commercialUse === 'yes'
          ? `${entry.license} - no usage restriction`
          : entry.commercialUse === 'no'
            ? `${entry.license} - non-commercial only`
            : `${entry.license} - check before any commercial use`}
      </p>
      {busy ? (
        <ProgressBar
          value={state.progress}
          label={`${state.status === 'compiling' ? 'Compiling' : 'Downloading'} ${state.message ?? ''}`}
        />
      ) : (
        <button
          type="button"
          className="button"
          disabled={unsupported !== null}
          onClick={() => onLoad(entry)}
        >
          {state.status === 'ready' ? 'Reload model' : 'Load model'}
        </button>
      )}
      {unsupported ? <p className="error">{unsupported}</p> : null}
      {state.status === 'error' ? <p className="error">{state.message}</p> : null}
      {state.status === 'ready' ? <p className="ok">Ready</p> : null}
    </div>
  );
}
